// FE-SCRM-052 — Opportunities list filter bar.
//
// Mirrors the Renewal Board's sidebar facets in a horizontal pill-row
// layout. Stage multi-select (the only filter that explicitly opts
// terminal stages back in), opportunity_type, lives_band,
// renewal_confidence, cadence_health, lost_reason, alerts_only, plus
// the closed_at date range. Producer / broker_location filters take
// UUIDs which a future chunk wires to a Membership / Location picker;
// for v1 they're URL-only.

import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import {
  CADENCE_HEALTH_LABEL,
  LIVES_BAND_LABEL,
  LOST_REASON_LABEL,
  RENEWAL_CONFIDENCE_LABEL,
  STAGE_LABEL,
  TYPE_LABEL,
} from "./labels";
import {
  hasActiveFilters,
  readFiltersFromUrl,
  writeFiltersToParams,
} from "./urlState";
import { EMPTY_FILTERS, type OpportunityListFilters } from "./types";


const STAGE_OPTIONS = [
  "lead", "meeting_set", "qualified", "proposal", "negotiation",
  "bor_signed_pending_effective_date", "closed_won", "closed_lost",
] as const;


const TYPE_OPTIONS = [
  "new_business", "bor_takeover", "rfp_response", "expansion",
] as const;


const LIVES_BAND_OPTIONS = [
  "micro", "small", "mid_market", "large", "enterprise",
] as const;


const CADENCE_OPTIONS = [
  "critical", "behind", "on_track", "ahead", "not_yet_urgent",
] as const;


const RENEWAL_CONFIDENCE_OPTIONS = [
  "confirmed", "inferred_from_form5500", "producer_estimate", "unknown",
] as const;


const LOST_REASON_OPTIONS = [
  "incumbent_retained", "lost_to_named_broker", "price", "no_decision",
  "account_dissolved", "account_acquired_by_parent",
  "service_concern", "relationship_concern", "compliance_concern",
  "coverage_gap_concern", "other",
] as const;


function toggle<T extends string>(values: T[], v: T): T[] {
  return values.includes(v) ? values.filter((x) => x !== v) : [...values, v];
}


export function FilterBar() {
  const [params, setParams] = useSearchParams();
  const filters = readFiltersFromUrl(params);

  const updateFilters = useCallback(
    (next: OpportunityListFilters) => {
      const out = writeFiltersToParams(next, params);
      // Filter changes reset pagination so the user doesn't land on
      // a page that no longer exists.
      out.delete("page");
      setParams(out, { replace: false });
    },
    [params, setParams],
  );

  return (
    <div
      data-testid="opportunities-filter-bar"
      className={[
        "flex flex-wrap items-start gap-x-4 gap-y-3",
        "rounded-md border border-border-subtle bg-raised p-3",
      ].join(" ")}
    >
      <ChipMulti
        testIdPrefix="stage"
        label="Stage"
        options={STAGE_OPTIONS as readonly string[]}
        labelFor={(v) => STAGE_LABEL[v] ?? v}
        value={filters.stage}
        onChange={(next) => updateFilters({ ...filters, stage: next })}
      />
      <ChipMulti
        testIdPrefix="type"
        label="Type"
        options={TYPE_OPTIONS as readonly string[]}
        labelFor={(v) => TYPE_LABEL[v] ?? v}
        value={filters.opportunity_type}
        onChange={(next) =>
          updateFilters({ ...filters, opportunity_type: next })
        }
      />
      <ChipMulti
        testIdPrefix="lives_band"
        label="Lives"
        options={LIVES_BAND_OPTIONS as readonly string[]}
        labelFor={(v) => LIVES_BAND_LABEL[v] ?? v}
        value={filters.lives_band}
        onChange={(next) =>
          updateFilters({
            ...filters,
            lives_band: next as OpportunityListFilters["lives_band"],
          })
        }
      />
      <ChipMulti
        testIdPrefix="cadence_health"
        label="Cadence"
        options={CADENCE_OPTIONS as readonly string[]}
        labelFor={(v) => CADENCE_HEALTH_LABEL[v] ?? v}
        value={filters.cadence_health}
        onChange={(next) =>
          updateFilters({
            ...filters,
            cadence_health: next as OpportunityListFilters["cadence_health"],
          })
        }
      />
      <ChipMulti
        testIdPrefix="renewal_confidence"
        label="Renewal confidence"
        options={RENEWAL_CONFIDENCE_OPTIONS as readonly string[]}
        labelFor={(v) => RENEWAL_CONFIDENCE_LABEL[v] ?? v}
        value={filters.renewal_confidence}
        onChange={(next) =>
          updateFilters({
            ...filters,
            renewal_confidence:
              next as OpportunityListFilters["renewal_confidence"],
          })
        }
      />
      {filters.stage.includes("closed_lost") && (
        <ChipMulti
          testIdPrefix="lost_reason"
          label="Lost reason"
          options={LOST_REASON_OPTIONS as readonly string[]}
          labelFor={(v) => LOST_REASON_LABEL[v] ?? v}
          value={filters.lost_reason}
          onChange={(next) =>
            updateFilters({
              ...filters,
              lost_reason: next as OpportunityListFilters["lost_reason"],
            })
          }
        />
      )}

      <DateRange
        label="Closed at"
        from={filters.closed_at_after}
        to={filters.closed_at_before}
        onChange={(from, to) =>
          updateFilters({
            ...filters,
            closed_at_after: from,
            closed_at_before: to,
          })
        }
      />

      <label
        data-testid="alerts-only-toggle"
        className="inline-flex items-center gap-2 text-xs text-text-secondary"
      >
        <input
          type="checkbox"
          checked={filters.alerts_only}
          onChange={(e) =>
            updateFilters({ ...filters, alerts_only: e.target.checked })
          }
          className="h-4 w-4 rounded border-gray-300 text-indigo-600"
        />
        Alerts only
      </label>

      {hasActiveFilters(filters) && (
        <button
          type="button"
          data-testid="reset-filters"
          onClick={() => updateFilters(EMPTY_FILTERS)}
          className={[
            "ml-auto text-xs font-medium text-text-tertiary",
            "underline hover:text-text-primary",
          ].join(" ")}
        >
          Reset filters
        </button>
      )}
    </div>
  );
}


function ChipMulti({
  testIdPrefix, label, options, labelFor, value, onChange,
}: {
  testIdPrefix: string;
  label: string;
  options: readonly string[];
  labelFor: (v: string) => string;
  value: readonly string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span
        className={[
          "text-[10px] font-semibold uppercase tracking-wide",
          "text-text-tertiary",
        ].join(" ")}
      >
        {label}
      </span>
      <div className="flex flex-wrap gap-1">
        {options.map((opt) => {
          const selected = value.includes(opt);
          return (
            <button
              type="button"
              key={opt}
              data-testid={`${testIdPrefix}-filter-${opt}`}
              aria-pressed={selected}
              onClick={() => onChange(toggle([...value], opt))}
              className={[
                "rounded-full border px-2 py-0.5 text-xs",
                selected
                  ? "border-teal bg-teal/10 text-teal"
                  : "border-border-subtle text-text-secondary "
                  + "hover:border-text-tertiary",
              ].join(" ")}
            >
              {labelFor(opt)}
            </button>
          );
        })}
      </div>
    </div>
  );
}


function DateRange({
  label, from, to, onChange,
}: {
  label: string;
  from: string | null;
  to: string | null;
  onChange: (from: string | null, to: string | null) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span
        className={[
          "text-[10px] font-semibold uppercase tracking-wide",
          "text-text-tertiary",
        ].join(" ")}
      >
        {label}
      </span>
      <div className="flex items-center gap-1 text-xs">
        <input
          type="date"
          data-testid="closed-at-after"
          aria-label="Closed at — from"
          value={from ?? ""}
          onChange={(e) => onChange(e.target.value || null, to)}
          className="rounded border border-border-subtle px-1.5 py-0.5"
        />
        <span className="text-text-tertiary">→</span>
        <input
          type="date"
          data-testid="closed-at-before"
          aria-label="Closed at — to"
          value={to ?? ""}
          onChange={(e) => onChange(from, e.target.value || null)}
          className="rounded border border-border-subtle px-1.5 py-0.5"
        />
      </div>
    </div>
  );
}
